import { useCallback, useEffect, useState } from "react";
import { ImagePlus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, imgSrc, uploadFile } from "@/lib/api";
import { Play } from "lucide-react";
import { toast } from "sonner";
import { PhotoTags } from "@/components/PhotoTags";
import { usePersons } from "@/hooks/usePersons";
import { t } from "@/lib/i18n";

const AddPhoto = ({ onAdded }) => {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [year, setYear] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const isVideo = file.type.startsWith("video/");
      if (isVideo && file.size > 100 * 1024 * 1024) throw new Error(t("Videos must be under 100 MB"));
      setProgress(0);
      const { path } = await uploadFile(file, isVideo ? "video" : "image", setProgress);
      await api.post("/album", { path, caption, year: year ? parseInt(year, 10) : null });
      toast.success(isVideo ? t("Video added to the album") : "Photo added to the album");
      setFile(null); setCaption(""); setYear(""); e.target.reset();
      onAdded();
    } catch (err) { toast.error(err.message && !err.response ? err.message : errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl bg-[#F4EDE2] p-6 grid md:grid-cols-[1fr_1.4fr_120px_auto] gap-3 items-center" data-testid="album-add-form">
      <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" required onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[#1E3A2B] file:text-white file:px-4 file:py-2" data-testid="album-file-input" />
      <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={t("Caption — who, where, what occasion")} className="h-11 rounded-xl bg-white" data-testid="album-caption-input" />
      <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder={t("Year")} className="h-11 rounded-xl bg-white" data-testid="album-year-input" />
      <Button type="submit" disabled={busy || !file} className="rounded-full bg-[#D96B43] hover:bg-[#C05731] h-11 px-6" data-testid="album-submit-btn">
        {busy ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{progress}%</span> : <><ImagePlus className="w-4 h-4 mr-2" />{t("Add")}</>}
      </Button>
    </form>
  );
};

export default function Album() {
  const { user } = useAuth();
  const [photos, setPhotos] = useState(null);
  const [open, setOpen] = useState(null);
  const { persons } = usePersons();
  const load = useCallback(() => api.get("/album").then(({ data }) => setPhotos(data)), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (ph) => {
    if (!window.confirm("Remove this photo from the album?")) return;
    try { await api.delete(`/album/${ph.photo_id}`); setOpen(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 space-y-10">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-3">{t("Family album")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight">{t("Old pictures, kept together.")}</h1>
        <p className="mt-3 text-[#52635B] max-w-2xl">Weddings, festivals, the old house — share the photos that tell the Vats story.</p>
      </div>
      {user?.can_vote ? <AddPhoto onAdded={load} /> : <p className="text-sm text-[#7E8E85]" data-testid="album-login-hint">Approved family members can add photos here.</p>}
      {photos === null ? <div className="h-64 rounded-3xl bg-[#F4EDE2] animate-pulse" /> : photos.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#CDBF9F] p-16 text-center text-[#7E8E85]" data-testid="album-empty">{t("No photos yet — be the first to add one.")}</div>
      ) : (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [&>*]:mb-4" data-testid="album-grid">
          {photos.map((ph) => (
            <button key={ph.photo_id} onClick={() => setOpen(ph)} className="block w-full break-inside-avoid group text-left" data-testid={`album-photo-${ph.photo_id}`}>
              {ph.media === "video" ? (
                <span className="relative block">
                  <video src={`${imgSrc(ph.url)}#t=0.5`} preload="metadata" muted playsInline className="w-full rounded-2xl border border-[#E5DEC9] bg-[#1C2421] transition-transform duration-300 group-hover:-translate-y-1" data-testid={`album-video-${ph.photo_id}`} />
                  <span className="absolute inset-0 grid place-items-center"><span className="w-14 h-14 rounded-full bg-white/90 grid place-items-center shadow-lg"><Play className="w-6 h-6 text-[#1E3A2B] fill-[#1E3A2B] ml-1" /></span></span>
                </span>
              ) : (
                <img src={imgSrc(ph.url)} alt={ph.caption} loading="lazy" className="w-full rounded-2xl border border-[#E5DEC9] transition-transform duration-300 group-hover:-translate-y-1" />
              )}
              {(ph.caption || ph.year) && <p className="mt-2 text-sm text-[#52635B]">{ph.caption} {ph.year && <span className="font-mono text-xs text-[#7E8E85]">· {ph.year}</span>}</p>}
              {ph.tagged.length > 0 && <p className="text-xs text-[#7E8E85] truncate">With {ph.tagged.map((x) => x.name).join(", ")}</p>}
            </button>
          ))}
        </div>
      )}
      {open && (
        <Dialog open onOpenChange={() => setOpen(null)}>
          <DialogContent className="sm:max-w-4xl" data-testid="album-lightbox">
            <DialogTitle className="font-serif">{open.caption || "Family photo"}</DialogTitle>
            {open.media === "video" ? (
              <video src={imgSrc(open.url)} controls autoPlay playsInline className="w-full max-h-[70vh] rounded-xl bg-black" data-testid="album-lightbox-video" />
            ) : (
              <img src={imgSrc(open.url)} alt={open.caption} className="w-full max-h-[70vh] object-contain rounded-xl" />
            )}
            <PhotoTags key={open.photo_id} photo={open} persons={persons} canTag={!!user?.can_vote} onChange={load} />
            <div className="flex justify-between items-center text-sm text-[#7E8E85]">
              <span>Shared by {open.uploader}{open.year ? ` · ${open.year}` : ""}</span>
              {(user?.is_admin || user?.user_id === open.uploaded_by) && (
                <Button variant="ghost" size="sm" className="text-[#C62828]" onClick={() => remove(open)} data-testid="album-delete-btn"><Trash2 className="w-4 h-4 mr-1" />Remove</Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
